import { EditIcon, UserIcon } from '~/svgs';
import { NextLink, Translate, useTranslate } from '~/i18n';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '~/components/common/button';
import SearchFilter from '../common/search-filter';
import { Section } from '../common/section';
import StatusFilter from '../coupon-list/status-filter';
import cn from 'classnames';
import { getStatus } from '.';
import { useRouter } from 'next/router';
import ApplicationSendSuccessModal from './application-send-success-modal';
import { errorMessage } from '~/utils/toasts';
import RejectionModal from './rejection-modal';
import { getRequestError, sendVisaApplication, useGetVisaRequestsDetails } from '~/apis/dhamen';
import { SubmitLoader } from '../common/loaders/submit';

export const ApplicantsPackageList = ({ nationalitiesList }: any) => {
   const { query } = useRouter();
   const id = query?.id as string;
   const [statusFilter, setStatusFilter] = useState<string | null>();
   const { translate, lang } = useTranslate();
   const [loading, setLoading] = useState<boolean>(false);
   const [isSuccess, setIsSuccess] = useState<boolean>(false);
   const [openApplicationSendSuccessModal, setApplicationSendSuccessModal] =
      useState<boolean>(false);
   const [openRejectionModal, setRejectionModal] = useState<boolean>(false);
   const [rejectionReasons, setRejectionReasons] = useState([]);
   const { data: details, refetch, isLoading } = useGetVisaRequestsDetails(id);
   const isRejected = details?.status.toLowerCase() === 'rejected';
   const isRequireUpdate = details?.status.toLowerCase() === 'requires update';
   const isUnderReview = details?.status.toLowerCase() === 'under review';
   const isApproved = details?.status.toLowerCase() === 'approved';

   const handleSendVisaApplication = async (id: string) => {
      try {
         setLoading(true);
         setIsSuccess(false);
         const { data, error } = await sendVisaApplication(id);
         if (data?.status.toLowerCase() === 'success') {
            setLoading(false);
            setIsSuccess(true);
            setApplicationSendSuccessModal(true);
         } else {
            setApplicationSendSuccessModal(false);
            throw new Error(
               data?.error ||
                  data?.original?.error ||
                  (error?.response?.data?.message?.toLowerCase() === 'visitors'
                     ? translate({ id: 'visasManager:missing information' })
                     : translate({ id: 'common:some error' }))
            );
         }
      } catch (error: any) {
         setLoading(false);
         setIsSuccess(false);
         errorMessage(error?.message);
      }
   };

   const getRejectionReasons = async (id: string) => {
      try {
         const data = await getRequestError(id);
         if (data?.status?.toLowerCase() === 'success') {
            setRejectionReasons(data?.data);
            setRejectionModal(true);
         }
      } catch (error) {
         console.log(error);
         errorMessage(translate({ id: 'common:some error' }));
      }
   };

   const sendApplicantsForRevew = () => {
      const visitorList = details?.visitors || [];
      for (const visitorObj of visitorList) {
         if (!visitorObj?.mofaquestionnaire?.length) {
            errorMessage(translate({ id: 'visasManager:missing information' }));
            return;
         }
      }
      setApplicationSendSuccessModal(true);
   };

   useEffect(() => {
      if (details?.status && (isRequireUpdate || isRequireUpdate)) {
         getRejectionReasons(id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [details, id]);

   const getNationalityLabel = useMemo(
      () => (nationalityCode: string) => {
         const nationality = nationalitiesList.find(
            (n: { value: string }) => n?.value === nationalityCode
         );
         return nationality ? nationality?.label : '';
      },
      [nationalitiesList]
   );

   return (
      <>
         <Section className="py-6 mt-5">
            <div className="px-4 space-y-5 lg:px-8">
               {isLoading ? (
                  <div className="flex justify-center h-36">
                     <SubmitLoader />
                  </div>
               ) : (
                  <div className="flex flex-col items-center justify-between gap-4 mb-4 text-black md:flex-row md:columns-2">
                     <div>
                        <div className="flex items-center gap-1 mb-2 text-sm font-medium">
                           <span
                              className={cn(
                                 'w-2 h-2 rounded-full flex',
                                 getStatus(details?.status, 'bg')
                              )}></span>
                           <span className={cn(getStatus(details?.status, 'text'))}>
                              {details?.status}
                           </span>
                        </div>
                        {isRejected || isRequireUpdate ? (
                           <div
                              className="mb-2 text-sm underline cursor-pointer text-primary w-fit"
                              onClick={() => setRejectionModal(true)}>
                              {isRejected && (
                                 <Translate id="visasManager:view reason for rejection" />
                              )}
                              {isRequireUpdate && (
                                 <Translate id="visasManager:view required update" />
                              )}
                           </div>
                        ) : null}

                        <div className="mb-3 text-xl font-bold sm:text-3xl">{details?.name}</div>
                        <div className="flex items-center gap-4 text-sm columns-2">
                           <div className="flex items-center gap-2 text-gray-500">
                              <UserIcon className="w-4 h-4 [&>*]:fill-gray-500" />
                              {details?.superVisorName}
                           </div>
                           {details?.applicationNo && (
                              <div className="text-gray-500">
                                 <Translate id="visasManager:request number" />
                                 {details?.applicationNo}
                              </div>
                           )}
                        </div>
                     </div>
                     <div className="flex items-start gap-4">
                        {!isApproved && (
                           <Button
                              className={cn(
                                 'w-full h-12 sm:w-60',
                                 (details?.visitors?.length >= 25 || isUnderReview) &&
                                    'cursor-not-allowed pointer-events-none'
                              )}
                              theme="outline-primary"
                              disabled={Boolean(details?.visitors?.length >= 25) || isUnderReview}>
                              <NextLink href={`/umrah-requests/${String(query.id)}/add-applicant`}>
                                 <Translate id="visasManager:add applicant" />
                              </NextLink>
                           </Button>
                        )}
                        {(details?.status?.toLowerCase() == 'submitted' || isRequireUpdate) &&
                        details?.visitors?.length ? (
                           <Button
                              onClick={() => sendApplicantsForRevew()}
                              className="w-full h-12 sm:w-60"
                              theme="primary"
                              loading={loading}>
                              <Translate id="visasManager:send visa applications for review" />
                           </Button>
                        ) : null}
                     </div>
                  </div>
               )}

               {!isUnderReview && (
                  <NextLink
                     href={`/umrah-requests/${String(
                        query.id
                     )}/edit/supervisor-and-arrival-details`}
                     className="flex items-center gap-1 mb-0 text-sm underline text-primary">
                     <EditIcon className="[&>*]:fill-primary w-4 h-4" />
                     <Translate id="common:edit" />
                  </NextLink>
               )}
            </div>
         </Section>
         <Section className="py-8 mt-5">
            <div className="px-4 space-y-5 lg:px-8">
               <div className="flex text-black ">
                  <div className="mb-3 text-xl font-bold sm:text-3xl">
                     <Translate id="visasManager:applicants" /> ({details?.visitors?.length || 0})
                  </div>
               </div>

               <div className="h-full">
                  <div className="flex flex-col mb-5 space-y-2 xs:space-y-0 xs:flex-row xxs:justify-between">
                     <div className="grid w-full grid-cols-2 gap-6 lg:grid-cols-4 lg:grid-rows-1">
                        <div className="col-span-3 lg:col-span-3">
                           <SearchFilter
                              onSearch={() => {}}
                              isLoading={false}
                              placeholder={translate({ id: 'visasManager:search applicants' })}
                           />
                        </div>

                        <div className="col-span-3 lg:col-span-1">
                           <StatusFilter
                              onChange={(status: string) => setStatusFilter(status)}
                              statusFilter={statusFilter || ''}
                           />
                        </div>
                     </div>
                  </div>

                  <div className="h-full overflow-auto">
                     {isLoading ? (
                        <div className="flex justify-center h-36">
                           <SubmitLoader />
                        </div>
                     ) : (
                        <div className="grid grid-cols-4">
                           <div className="col-span-4">
                              <table className="w-full text-sm min-w-[550px]">
                                 <thead className="uppercase">
                                    <tr>
                                       <th className="px-4 text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="visasManager:applicant name" />
                                       </th>

                                       <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="visasManager:nationality" />
                                       </th>
                                       <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="visasManager:passport number" />
                                       </th>

                                       <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                          <Translate id="visasManager:status" />
                                       </th>

                                       {details?.visitors?.some?.(
                                          (item: { inquiryStatus: string }) => item.inquiryStatus
                                       ) ? (
                                          <th className="text-left text-gray-400 bg-gray-100 rtl:text-right h-11 first:rounded-md last:rounded-md">
                                             <Translate id="visasManager:visa number" />
                                          </th>
                                       ) : null}

                                       <th className="px-4 text-right text-gray-400 bg-gray-100 rtl:text-left h-11 first:rounded-md last:rounded-md">
                                          <Translate id="visasManager:actions" />
                                       </th>
                                    </tr>
                                    {details?.visitors?.length == 0 ? (
                                       <tr>
                                          <td colSpan={5} className="py-6 text-center">
                                             <span className="w-full py-8 text-xs text-center xl:text-sm text-grey-200">
                                                <Translate id="visasManager:no applicants" />
                                             </span>
                                          </td>
                                       </tr>
                                    ) : null}
                                 </thead>

                                 <tbody>
                                    {details?.visitors?.map((visitor: any, index: string) => {
                                       const firstName =
                                          lang == 'ar'
                                             ? visitor?.firstNameAr || visitor?.firstNameEn
                                             : visitor?.firstNameEn;
                                       const lastName =
                                          lang == 'ar'
                                             ? visitor?.familyNameAr || visitor?.familyNameEn
                                             : visitor?.familyNameEn;
                                       const fullName = `${firstName} ${lastName}` ?? '';
                                       return (
                                          <tr
                                             className="border-b border-gray-200 border-dashed hover:bg-gray-50"
                                             key={index}>
                                             <td className="py-4 ltr:pl-4 rtl:pr-4">{fullName}</td>
                                             <td className="py-4">
                                                {getNationalityLabel(visitor?.nationality)}
                                             </td>
                                             <td className="py-4">{visitor?.passportNo}</td>
                                             <td className="py-4">
                                                <div className="flex items-center gap-1">
                                                   <span
                                                      className={cn(
                                                         'w-2 h-2 rounded-full flex',
                                                         getStatus(
                                                            visitor?.inquiryStatus ||
                                                               details?.status,
                                                            'bg'
                                                         )
                                                      )}></span>
                                                   <span
                                                      className={cn(
                                                         getStatus(
                                                            visitor?.inquiryStatus ||
                                                               details?.status,
                                                            'text'
                                                         )
                                                      )}>
                                                      {visitor?.inquiryStatus || details?.status}
                                                   </span>
                                                </div>
                                             </td>
                                             {details?.visitors?.some?.(
                                                (item: { inquiryStatus: string }) =>
                                                   item.inquiryStatus
                                             ) ? (
                                                <td className="py-4">
                                                   {visitor?.inquiryVisaNumber || ''}
                                                </td>
                                             ) : null}
                                             <td className="py-4 ltr:pr-4 rtl:pl-4 ltr:text-right rtl:text-left">
                                                <div className="flex items-center justify-end w-full gap-2">
                                                   <Button
                                                      className={cn(
                                                         'w-full h-12',
                                                         isUnderReview &&
                                                            'cursor-not-allowed pointer-events-none opacity-40'
                                                      )}
                                                      disabled={isUnderReview}>
                                                      <NextLink
                                                         href={`/umrah-requests/${id}/add-applicant/${visitor?.applicationNo}/edit/applicant-information`}>
                                                         <div className="flex items-center justify-center gap-2 text-sm font-semibold text-grey-100 h-fit">
                                                            <EditIcon />
                                                            <Translate id="common:edit" />
                                                         </div>
                                                      </NextLink>
                                                   </Button>
                                                   {/* <Button
                                                   className="flex items-center !text-sm font-semibold !p-0"
                                                   onClick={() => {}}>
                                                   <div className="flex items-center gap-2 text-states-warning h-fit">
                                                      <TrashIconOutline />
                                                      <Translate id="common:delete" />
                                                   </div>
                                                </Button> */}
                                                </div>
                                             </td>
                                          </tr>
                                       );
                                    })}
                                 </tbody>
                              </table>
                           </div>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </Section>
         {/* <div className="flex justify-center my-5">
               <Pagination
                  back={() => setPageNumber(prev => prev - 1)}
                  next={() => setPageNumber(prev => prev + 1)}
                  currentPage={QueryData.meta.current_page}
                  lastPage={QueryData.meta.last_page}
                  numberOfElementsInOnePage={QueryData.meta.per_page}
                  totalElements={QueryData.meta.total}
               />
            </div> */}
         <ApplicationSendSuccessModal
            open={openApplicationSendSuccessModal}
            closeModal={() => {
               setApplicationSendSuccessModal(false);
            }}
            sendApplication={() => {
               handleSendVisaApplication(id);
               refetch();
            }}
            isSuccess={isSuccess}
            loading={loading}
            applicants={details?.visitors?.length as string}
         />
         <RejectionModal
            open={openRejectionModal}
            closeModal={() => setRejectionModal(false)}
            reasons={rejectionReasons}
            isRequireUpdate={isRequireUpdate}
         />
      </>
   );
};
